import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { publicPortalApi } from '@/lib/api/portal.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import { PortalProgress } from '@/components/portal/PortalProgress.js';

export function PortalTokenViewPage() {
  const { token } = useParams<{ token: string }>();
  const query = useQuery({
    queryKey: ['public-portal-ticket', token],
    queryFn: () => publicPortalApi.ticketByToken(token!),
    enabled: Boolean(token),
  });

  const data = query.data as {
    ticketNumber: string;
    statusLabel: string;
    statusDescription: string;
    deviceSummary: string;
    repairSummary: string;
    promisedDate: string | null;
    balanceDue: string;
    progressIndex: number;
    customerComplaint?: string | null;
    storeInfo?: {
      name: string;
      phone: string | null;
      address: string;
      logoUrl?: string | null;
    } | null;
  } | undefined;

  if (query.isLoading) {
    return (
      <PublicShell>
        <p className="text-center text-slate-500">Loading…</p>
      </PublicShell>
    );
  }

  if (!data) {
    return (
      <PublicShell>
        <p className="text-center text-slate-500">Repair not found.</p>
      </PublicShell>
    );
  }

  return (
    <PublicShell storeName={data.storeInfo?.name} logoUrl={data.storeInfo?.logoUrl}>
      <h1 className="text-center text-xl font-bold text-slate-950">Track Your Repair</h1>
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="text-lg font-bold">{data.ticketNumber}</div>
        <p className="mt-1 text-sm text-slate-600">
          {data.deviceSummary}
          {data.repairSummary ? ` — ${data.repairSummary}` : ''}
        </p>
        {data.customerComplaint?.trim() ? (
          <div className="mt-3 rounded-xl bg-slate-50 p-3">
            <div className="text-xs font-bold uppercase text-slate-500">Your issue</div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{data.customerComplaint.trim()}</p>
          </div>
        ) : null}
        <div className="mt-4 rounded-xl bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-800">
          Status: 🔵 {data.statusLabel.toUpperCase()}
        </div>
        <p className="mt-2 text-sm text-slate-600">{data.statusDescription}</p>
        <div className="mt-4">
          <PortalProgress index={data.progressIndex} />
        </div>
        {data.promisedDate ? (
          <p className="mt-3 text-sm text-slate-600">
            Estimated Ready: {formatDateTime(data.promisedDate)}
          </p>
        ) : null}
        {Number(data.balanceDue) > 0 ? (
          <p className="mt-1 text-sm font-semibold">Amount Due: {formatCurrency(data.balanceDue)}</p>
        ) : null}
      </div>
      {data.storeInfo ? (
        <p className="mt-6 text-center text-sm text-slate-500">
          Questions? Call us: {data.storeInfo.phone}
          <br />
          {data.storeInfo.name}
          {data.storeInfo.address ? ` — ${data.storeInfo.address}` : ''}
        </p>
      ) : null}
    </PublicShell>
  );
}

export function PublicShell({
  children,
  storeName,
  logoUrl,
}: {
  children: ReactNode;
  storeName?: string;
  logoUrl?: string | null;
}) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-slate-50 px-4 py-8">
      <div className="mx-auto max-w-md">
        <div className="mb-6 text-center">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="mx-auto h-12 object-contain" />
          ) : (
            <div className="text-lg font-bold text-[#1B4F8A]">{storeName ?? 'Repair Status'}</div>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}
